import { useEffect, useState } from 'react';
import { api } from '../../shell/bridge';
import { AgentSettings } from '../AI/AgentSettings';
import './settings.css';

type Toggles = Record<string, boolean>;

interface Props {
  onClose: () => void;
  onLeave: () => void;
}

/**
 * Settings screen (card 0xhNPdS42FTh, Jonathan 2026-10-02).
 *
 * Holds what used to be scattered: the agent involvement control (moved here
 * verbatim from the AI screen — one import, one JSX tag, exactly as the
 * component's own docblock promised) and the Leave action, which moved out of
 * the top bar and became a destructive row with a confirm step, because
 * leaving a couple is not reversible from the client.
 *
 * `visible` on AgentSettings keeps its original rule: no permission, no
 * behaviour control. Permissions themselves stay on the AI tab — this screen
 * only reads them.
 */
export function Settings({ onClose, onLeave }: Props) {
  const [toggles, setToggles] = useState<Toggles | null>(null);
  const [failed, setFailed] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let alive = true;
    api<{ toggles: Toggles }>('/api/ai/toggles')
      .then(data => {
        if (!alive) return;
        setToggles(data.toggles ?? {});
        setFailed(false);
      })
      .catch(err => {
        console.error('[Pear] settings: toggles load failed', err);
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const anyPermission = toggles ? Object.values(toggles).some(Boolean) : false;

  return (
    <div className="set-panel" id="settings-screen">
      <div className="set-head">
        <h2 className="set-title">Settings</h2>
        <button
          type="button"
          className="pear-btn set-done"
          id="btn-settings-done"
          onClick={onClose}
        >
          Done
        </button>
      </div>

      <section className="set-section" aria-labelledby="set-agent-title">
        <h3 className="set-section-title" id="set-agent-title">
          Agent
        </h3>
        <p className="set-section-note">
          Permissions live in the AI tab — turn one on there and the control
          below appears.
        </p>

        {toggles === null && !failed && (
          <p className="set-loading" aria-busy="true">
            Loading…
          </p>
        )}
        {failed && (
          <p className="set-error">
            Couldn’t load your permissions. Open the AI tab and try again.
          </p>
        )}
        {toggles !== null && !anyPermission && (
          <p className="set-empty">Every permission is off — nothing to configure yet.</p>
        )}

        <AgentSettings visible={anyPermission} />
      </section>

      <section className="set-section" aria-labelledby="set-danger-title">
        <h3 className="set-section-title set-section-title-danger" id="set-danger-title">
          Leaving
        </h3>

        {!confirming && (
          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Leave this couple</div>
              <div className="set-note">
                Your partner keeps the space. This device loses access straight
                away.
              </div>
            </div>
            <button
              type="button"
              className="pear-btn pear-btn-blush set-row-btn"
              id="btn-leave"
              onClick={() => setConfirming(true)}
            >
              Leave
            </button>
          </div>
        )}

        {confirming && (
          <div className="set-confirm" role="group" aria-label="Confirm leaving">
            <p className="set-confirm-copy">
              Leave for good? You can’t undo this from here — rejoining needs a
              new pair code from your partner.
            </p>
            <div className="set-confirm-actions">
              <button
                type="button"
                className="pear-btn set-cancel"
                id="btn-leave-cancel"
                onClick={() => setConfirming(false)}
              >
                Stay
              </button>
              <button
                type="button"
                className="pear-btn pear-btn-blush"
                id="btn-leave-confirm"
                onClick={onLeave}
              >
                Leave couple
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
